import type { IntlShape } from 'react-intl';

import type { ContextualToolbar, ToolbarItem } from '@atlaskit/editor-common/extensions';
import { type EditorActions } from '@atlaskit/editor-core';

import { i18n } from '../messages';

type ChartToolbar = {
	editorActions?: EditorActions;
	externalToolbarItems?: ToolbarItem[];
	intl: IntlShape;
};

// eslint-disable-next-line import/dynamic-import-chunkname
const editSourceIcon = () => import('@atlaskit/icon/core/grid');

export const chartToolbar = ({
	intl,
	editorActions,
	externalToolbarItems = [],
}: ChartToolbar): ContextualToolbar => ({
	context: {
		type: 'extension',
		nodeType: 'extension',
		extensionKey: 'chart:default',
		extensionType: 'com.atlassian.chart',
	},
	toolbarItems: (adf: any, _api: any) => {
		if (!editorActions) {
			return [];
		}

		const { sources = [] } = ((adf.marks || []).find((mark: any) => mark.type === 'dataConsumer')
			?.attrs || {}) as { sources?: string[] };

		const hasSource = sources.some(
			(source: string) =>
				editorActions.getNodeByFragmentLocalId(source) || editorActions.getNodeByLocalId(source),
		);

		return [
			{
				key: 'edit-chart',
				display: 'label',
				label: intl.formatMessage(i18n.chartOptionsLabel),
				tooltip: intl.formatMessage(i18n.chartOptionsTooltip),
				disabled: !hasSource,
				action: async (_adf: any, api: any) => {
					return api.editInContextPanel(
						(parameters: any) => parameters,
						(parameters: any) => Promise.resolve(parameters),
					);
				},
			},
			{
				key: 'edit-source-table',
				display: 'icon-and-label',
				label: intl.formatMessage(i18n.editSourceLabel),
				disabled: !hasSource,
				tooltip: intl.formatMessage(i18n.editSourceTooltip),
				icon: editSourceIcon,
				action: async (_adf, api) => {
					if (!hasSource) {
						throw new Error('Table source is not found');
					}

					// sourceId can be either localId or fragment localId
					let sourceId = sources[0];
					// search the nodes if there's a fragment mark with matching source id then use its localId to scroll
					const nodeByFragmentLocalId = editorActions.getNodeByFragmentLocalId(sourceId);

					if (nodeByFragmentLocalId) {
						sourceId = nodeByFragmentLocalId.attrs.localId;
					}

					api.doc.scrollTo(sourceId);
				},
			},
			...externalToolbarItems,
		];
	},
});
